import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText,
  Lock,
  Mail,
  Building,
  Phone,
  MapPin,
  Gift,
  ChevronRight,
  ArrowLeft,
  CheckCircle2,
  Shield,
  Eye,
  EyeOff,
  Loader2,
  Star,
  BarChart3,
  Euro,
  Search,
  GitBranch,
  PieChart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1);
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'register') {
      setActiveTab('register');
    }
  }, [searchParams]);

  // SIRENE search state
  const [sireneQuery, setSireneQuery] = useState('');
  const [sireneResults, setSireneResults] = useState([]);
  const [sireneLoading, setSireneLoading] = useState(false);
  const [showSireneDropdown, setShowSireneDropdown] = useState(false);
  const [sireneSelected, setSireneSelected] = useState(false);
  const sireneTimerRef = useRef(null);

  const searchSirene = (query) => {
    setSireneQuery(query);
    setSireneSelected(false);
    if (sireneTimerRef.current) clearTimeout(sireneTimerRef.current);
    if (query.length < 3) { setSireneResults([]); setShowSireneDropdown(false); return; }
    sireneTimerRef.current = setTimeout(async () => {
      setSireneLoading(true);
      try {
        const res = await fetch(`${API_URL}/sirene/search?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        setSireneResults(data.results || []);
        setShowSireneDropdown(true);
      } catch (e) { setSireneResults([]); }
      finally { setSireneLoading(false); }
    }, 400);
  };

  const selectSireneCompany = (company) => {
    setRegisterData(prev => ({
      ...prev,
      structure_nom: company.nom || prev.structure_nom,
      structure_siret: company.siret || prev.structure_siret,
      structure_adresse: company.adresse || prev.structure_adresse,
      structure_code_postal: company.code_postal || prev.structure_code_postal,
      structure_ville: company.ville || prev.structure_ville,
    }));
    setSireneQuery(company.nom || '');
    setSireneSelected(true);
    setSireneResults([]);
    setShowSireneDropdown(false);
  };

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    structure_nom: '',
    structure_siret: '',
    structure_adresse: '',
    structure_code_postal: '',
    structure_ville: '',
    structure_telephone: '',
    structure_email: '',
    admin_email: '',
    admin_password: '',
    admin_nom: '',
    admin_prenom: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    const result = await login(loginData.email, loginData.password);
    if (result.success) {
      if (result.subscriptionStatus?.status === 'trial_expired') {
        navigate('/subscription');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (step === 1) {
      if (!registerData.structure_nom || !registerData.structure_siret ||
          !registerData.structure_adresse || !registerData.structure_code_postal ||
          !registerData.structure_ville || !registerData.structure_telephone ||
          !registerData.structure_email) {
        setError('Veuillez remplir tous les champs de la structure');
        return;
      }
      setStep(2);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    const result = await register(registerData);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess('Un code de verification a ete envoye a votre adresse email.');
        setForgotStep(2);
      } else {
        setError(data.detail || 'Une erreur est survenue.');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur.');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      setLoading(false);
      return;
    }
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caracteres.');
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail, code: resetCode, new_password: newPassword })
      });
      const data = await response.json();
      if (response.ok) {
        setForgotStep(3);
        setSuccess('Mot de passe reinitialise avec succes !');
      } else {
        setError(data.detail || 'Une erreur est survenue.');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur.');
    }
    setLoading(false);
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setForgotEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const benefits = [
    { icon: FileText, title: "Factures fournisseurs", desc: "Saisie et suivi centralises" },
    { icon: GitBranch, title: "Workflows de validation", desc: "Circuits d'approbation flexibles" },
    { icon: Euro, title: "Virements SEPA", desc: "Paiements automatises" },
    { icon: BarChart3, title: "Rapports detailles", desc: "Tableaux de bord en temps reel" },
    { icon: PieChart, title: "Analytique", desc: "Ventilation multi-axes" },
    { icon: Shield, title: "Conformite RGPD", desc: "Donnees securisees en France" }
  ];

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative bg-gradient-to-br from-[#0F5156] via-[#0d464a] to-[#0a3538] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="absolute top-20 -left-20 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#C27868]/10 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <button onClick={() => navigate('/')} className="flex items-center gap-3 group transition-transform hover:scale-105">
              <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <span className="text-2xl font-bold text-white tracking-tight block">Nexus Compta</span>
                <span className="text-white/60 text-sm">Gestion comptable simplifiee</span>
              </div>
            </button>
          </div>

          <div className="space-y-10">
            <div>
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
                Simplifiez votre
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-white to-[#C27868]">
                  comptabilité fournisseurs
                </span>
              </h1>
              <p className="text-lg text-white/70 max-w-lg leading-relaxed">
                La solution complete pour gerer vos factures, validations et paiements fournisseurs.
                Concue pour les TPE et PME francaises.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {benefits.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-cyan-300" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium text-sm">{item.title}</h3>
                    <p className="text-white/50 text-xs mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {['MD', 'PL', 'SB', 'JD'].map((initials, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-white/20 to-white/10 border-2 border-[#0F5156] flex items-center justify-center text-xs font-medium text-white">
                      {initials}
                    </div>
                  ))}
                </div>
                <div className="ml-2">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-white/50 text-xs">500+ entreprises</p>
                </div>
              </div>
              <div className="h-8 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-white/70 text-sm">Heberge en France</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-green-500/20 to-emerald-500/10 border border-green-400/20 backdrop-blur-sm">
            <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Gift className="w-7 h-7 text-green-300" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-white text-lg">7 jours d'essai gratuit</p>
              <p className="text-white/60 text-sm">Sans engagement - Sans carte bancaire - Toutes fonctionnalites</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-8 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-500 hover:text-[#0F5156] transition-colors mb-6 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Retour a l'accueil
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#0F5156] flex items-center justify-center shadow-lg">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-slate-900 block">Nexus Compta</span>
                <span className="text-slate-500 text-sm">Gestion comptable simplifiee</span>
              </div>
            </div>
          </div>

          <button onClick={() => navigate('/')} className="hidden lg:flex items-center gap-2 text-slate-500 hover:text-[#0F5156] transition-colors mb-8 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            Retour a l'accueil
          </button>

          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              setActiveTab(value);
              setError('');
              setSuccess('');
              setShowForgotPassword(false);
              setForgotStep(1);
              if (value === 'register') setStep(1);
            }}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2 mb-8 h-14 p-1.5 bg-slate-100 rounded-xl">
              <TabsTrigger value="login" className="text-base font-medium rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Connexion
              </TabsTrigger>
              <TabsTrigger value="register" className="text-base font-medium rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Inscription
              </TabsTrigger>
            </TabsList>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-500 text-xs font-bold">!</span>
                </div>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-100 rounded-xl text-green-700 text-sm flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>{success}</span>
              </div>
            )}

            {/* Login Tab */}
            <TabsContent value="login" className="mt-0">
              {showForgotPassword ? (
                <div className="space-y-6">
                  <div>
                    <button type="button" onClick={resetForgotPassword} className="flex items-center gap-2 text-slate-500 hover:text-[#0F5156] transition-colors mb-4 text-sm font-medium">
                      <ArrowLeft className="w-4 h-4" />
                      Retour a la connexion
                    </button>
                    <h2 className="text-2xl font-bold text-slate-900">
                      {forgotStep === 1 && 'Mot de passe oublie'}
                      {forgotStep === 2 && 'Entrez le code'}
                      {forgotStep === 3 && 'Mot de passe reinitialise'}
                    </h2>
                    <p className="text-slate-500 mt-1">
                      {forgotStep === 1 && 'Saisissez votre adresse email pour recevoir un code de reinitialisation'}
                      {forgotStep === 2 && 'Un code a 6 chiffres a ete envoye a votre adresse email'}
                      {forgotStep === 3 && 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe'}
                    </p>
                  </div>

                  {forgotStep === 1 && (
                    <form onSubmit={handleForgotPassword} className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="forgot-email" className="text-sm font-medium text-slate-700">Adresse email</Label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input id="forgot-email" type="email" placeholder="nom@entreprise.fr" className="pl-12 h-13 text-base border-slate-200" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
                        </div>
                      </div>
                      <Button type="submit" className="w-full h-13 text-base font-semibold bg-[#0F5156] hover:bg-[#0d464a]" disabled={loading}>
                        {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Envoi en cours...</> : <>Envoyer le code<ChevronRight className="w-5 h-5 ml-2" /></>}
                      </Button>
                    </form>
                  )}

                  {forgotStep === 2 && (
                    <form onSubmit={handleResetPassword} className="space-y-5">
                      <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                        <p className="text-sm text-blue-700">Code envoye a <strong>{forgotEmail}</strong></p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="reset-code" className="text-sm font-medium text-slate-700">Code de verification</Label>
                        <Input id="reset-code" type="text" placeholder="000000" className="h-13 text-center text-2xl tracking-[0.5em] font-mono border-slate-200" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-password" className="text-sm font-medium text-slate-700">Nouveau mot de passe</Label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input id="new-password" type={showNewPassword ? 'text' : 'password'} placeholder="Minimum 8 caracteres" className="pl-12 pr-12 h-13 text-base border-slate-200" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} />
                          <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password" className="text-sm font-medium text-slate-700">Confirmer le mot de passe</Label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input id="confirm-password" type={showNewPassword ? 'text' : 'password'} placeholder="Confirmez le mot de passe" className="pl-12 h-13 text-base border-slate-200" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} />
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button type="button" variant="outline" className="flex-1 h-12" onClick={() => { setForgotStep(1); setError(''); setSuccess(''); }}>
                          <ArrowLeft className="w-4 h-4 mr-2" />Retour
                        </Button>
                        <Button type="submit" className="flex-1 h-12 bg-[#0F5156] hover:bg-[#0d464a]" disabled={loading || resetCode.length !== 6}>
                          {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Envoi...</> : 'Reinitialiser'}
                        </Button>
                      </div>
                    </form>
                  )}

                  {forgotStep === 3 && (
                    <div className="space-y-6">
                      <div className="flex justify-center">
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle2 className="w-8 h-8 text-green-600" />
                        </div>
                      </div>
                      <Button onClick={resetForgotPassword} className="w-full h-13 text-base font-semibold bg-[#0F5156] hover:bg-[#0d464a]">
                        Se connecter<ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Bon retour parmi nous</h2>
                    <p className="text-slate-500 mt-1">Connectez-vous pour acceder a votre espace comptable</p>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-sm font-medium text-slate-700">Adresse email</Label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input id="login-email" type="email" placeholder="nom@entreprise.fr" className="pl-12 h-13 text-base border-slate-200" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} required />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password" className="text-sm font-medium text-slate-700">Mot de passe</Label>
                        <button type="button" className="text-sm text-[#0F5156] hover:underline font-medium" onClick={() => { setShowForgotPassword(true); setError(''); setSuccess(''); }}>
                          Mot de passe oublie ?
                        </button>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input id="login-password" type={showPassword ? 'text' : 'password'} placeholder="Votre mot de passe" className="pl-12 pr-12 h-13 text-base border-slate-200" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <Button type="submit" className="w-full h-13 text-base font-semibold bg-[#0F5156] hover:bg-[#0d464a] shadow-lg shadow-[#0F5156]/25" disabled={loading}>
                      {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Connexion en cours...</> : <>Se connecter<ChevronRight className="w-5 h-5 ml-2" /></>}
                    </Button>
                  </form>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                    <div className="relative flex justify-center text-sm"><span className="px-4 bg-slate-50 text-slate-500">Pas encore de compte ?</span></div>
                  </div>

                  <Button variant="outline" className="w-full h-12 font-medium border-slate-200 hover:bg-slate-100" onClick={() => setActiveTab('register')}>
                    Créer ma structure gratuitement
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register" className="mt-0">
              <div className="space-y-6">
                {/* Progress */}
                <div className="flex items-center gap-3">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-xl text-sm font-semibold transition-all ${step >= 1 ? 'bg-[#0F5156] text-white shadow-lg shadow-[#0F5156]/25' : 'bg-slate-100 text-slate-500'}`}>
                    {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
                  </div>
                  <div className={`flex-1 h-1.5 rounded-full transition-all ${step > 1 ? 'bg-[#0F5156]' : 'bg-slate-200'}`} />
                  <div className={`flex items-center justify-center w-10 h-10 rounded-xl text-sm font-semibold transition-all ${step >= 2 ? 'bg-[#0F5156] text-white shadow-lg shadow-[#0F5156]/25' : 'bg-slate-100 text-slate-500'}`}>
                    2
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    {step === 1 ? 'Informations entreprise' : 'Créer votre compte'}
                  </h2>
                  <p className="text-slate-500 mt-1">
                    {step === 1 ? 'Étape 1/2 - Renseignez les informations de votre structure' : 'Étape 2/2 - Finalisez votre inscription'}
                  </p>
                </div>

                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-100 rounded-xl">
                  <Gift className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-700">7 jours d'essai gratuit - Sans carte bancaire</span>
                </div>

                <form onSubmit={handleRegister} className="space-y-4">
                  {step === 1 ? (
                    <>
                      {/* SIRENE search */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">
                          <span className="flex items-center gap-1.5">
                            <Search className="w-3.5 h-3.5 text-[#0F5156]" />
                            Rechercher votre entreprise (nom ou SIREN)
                          </span>
                        </Label>
                        <div className="relative">
                          <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input placeholder="Tapez le nom ou le SIREN..." className="pl-12 h-12 border-slate-200" value={sireneQuery} onChange={(e) => searchSirene(e.target.value)} onFocus={() => { if (sireneResults.length > 0) setShowSireneDropdown(true); }} />
                          {sireneLoading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0F5156] animate-spin" />}
                          {showSireneDropdown && sireneResults.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                              {sireneResults.map((company, idx) => (
                                <button key={company.siret || idx} type="button" className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors" onClick={() => selectSireneCompany(company)}>
                                  <div className="font-medium text-slate-900 text-sm">{company.nom}</div>
                                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                                    <span>SIRET: {company.siret}</span>
                                    {company.ville && <span>- {company.code_postal} {company.ville}</span>}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {sireneSelected && (
                          <div className="flex items-center gap-2 text-xs text-green-600">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Informations importees automatiquement
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Nom de la structure *</Label>
                        <div className="relative">
                          <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input placeholder="Ma Societe SARL" className="pl-12 h-12 border-slate-200" value={registerData.structure_nom} onChange={(e) => setRegisterData({ ...registerData, structure_nom: e.target.value })} required />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">SIRET *</Label>
                        <Input placeholder="123 456 789 00012" className="h-12 border-slate-200 font-mono" value={registerData.structure_siret} onChange={(e) => setRegisterData({ ...registerData, structure_siret: e.target.value })} required maxLength={14} />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Adresse *</Label>
                        <div className="relative">
                          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input placeholder="12 rue de la Paix" className="pl-12 h-12 border-slate-200" value={registerData.structure_adresse} onChange={(e) => setRegisterData({ ...registerData, structure_adresse: e.target.value })} required />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700">Code postal *</Label>
                          <Input placeholder="75001" className="h-12 border-slate-200" value={registerData.structure_code_postal} onChange={(e) => setRegisterData({ ...registerData, structure_code_postal: e.target.value })} required maxLength={5} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700">Ville *</Label>
                          <Input placeholder="Paris" className="h-12 border-slate-200" value={registerData.structure_ville} onChange={(e) => setRegisterData({ ...registerData, structure_ville: e.target.value })} required />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Telephone *</Label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input placeholder="01 23 45 67 89" className="pl-12 h-12 border-slate-200" value={registerData.structure_telephone} onChange={(e) => setRegisterData({ ...registerData, structure_telephone: e.target.value })} required />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Email de la structure *</Label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input type="email" placeholder="contact@masociete.fr" className="pl-12 h-12 border-slate-200" value={registerData.structure_email} onChange={(e) => setRegisterData({ ...registerData, structure_email: e.target.value })} required />
                        </div>
                      </div>

                      <Button type="submit" className="w-full h-13 text-base font-semibold bg-[#0F5156] hover:bg-[#0d464a] shadow-lg shadow-[#0F5156]/25">
                        Continuer<ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700">Prenom *</Label>
                          <Input placeholder="Jean" className="h-12 border-slate-200" value={registerData.admin_prenom} onChange={(e) => setRegisterData({ ...registerData, admin_prenom: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700">Nom *</Label>
                          <Input placeholder="Dupont" className="h-12 border-slate-200" value={registerData.admin_nom} onChange={(e) => setRegisterData({ ...registerData, admin_nom: e.target.value })} required />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Email administrateur *</Label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input type="email" placeholder="jean.dupont@masociete.fr" className="pl-12 h-12 border-slate-200" value={registerData.admin_email} onChange={(e) => setRegisterData({ ...registerData, admin_email: e.target.value })} required />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Mot de passe *</Label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <Input type={showAdminPassword ? 'text' : 'password'} placeholder="Minimum 8 caracteres" className="pl-12 pr-12 h-12 border-slate-200" value={registerData.admin_password} onChange={(e) => setRegisterData({ ...registerData, admin_password: e.target.value })} required minLength={8} />
                          <button type="button" onClick={() => setShowAdminPassword(!showAdminPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {showAdminPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button type="button" variant="outline" className="flex-1 h-12 font-medium border-slate-200" onClick={() => { setStep(1); setError(''); }}>
                          <ArrowLeft className="w-4 h-4 mr-2" />Retour
                        </Button>
                        <Button type="submit" className="flex-1 h-12 font-semibold bg-[#0F5156] hover:bg-[#0d464a] shadow-lg shadow-[#0F5156]/25" disabled={loading}>
                          {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Création...</> : <>Créer mon compte<ChevronRight className="w-5 h-5 ml-2" /></>}
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Login;
